import React, { FC } from 'react'
import { Redirect } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import {
    Grid,
    Divider,
    styled,
    makeStyles,
} from '@material-ui/core'

import GridContainer from '../../../@jumbo/components/GridContainer'
import PageContainer from '../../../@jumbo/components/PageComponents/layouts/PageContainer'
import Loading from '../../../components/Common/Loading'

import Notifications from './Notifications'

import { fetchReservationDetails } from '@/redux/actions/Reservation'

import { RESERVATION_NOT_FOUND } from '@/services/api/errorCodes'
import ReservationInfo from '@/routes/Pages/ReservationDetail/ReservationInfo'
import PropertyInfo from '@/routes/Pages/ReservationDetail/PropertyInfo'
import CustomerInfo from '@/routes/Pages/ReservationDetail/CustomerInfo'
import AccessInfo from '@/routes/Pages/ReservationDetail/access/AccessInfo'
import IdVerificationInfo from '@/routes/Pages/ReservationDetail/IdVerificationInfo'
import { useDocumentTitle } from '@/hooks/use-document-title'

const breadcrumbs = [
    { label: 'houston', link: '/' },
    { label: 'reservation', isActive: true },
]

export const StyledDivider = styled(Divider)({
    marginBottom: '10px',
    marginTop: '10px',
})

const useStyles = makeStyles(theme => ({
    pageWrapper: {
        paddingTop: 30,
        '& .MuiTextField-root': {
            marginRight: theme.spacing(10),
            marginTop: theme.spacing(5),
        },
        '& h1': {
            fontSize: '25px',
        },
        [theme.breakpoints.up('md')]: {
            paddingLeft: 50,
            paddingRight: 50,
        },
        [theme.breakpoints.up('lg')]: {
            paddingLeft: 65,
            paddingRight: 65,
        },
        [theme.breakpoints.up('xl')]: {
            paddingLeft: 88,
            paddingRight: 88,
        },
        [theme.breakpoints.down('sm')]: {
            paddingTop: 20,
            paddingBottom: 20,
        },
    },
}))

interface Props {
    readonly reservationId: string;
}

const ReservationDetails: FC<Props> = ({ reservationId }) => {
    const classes = useStyles()

    const dispatch = useDispatch()
    // @ts-ignore revisit types
    const reservationState = useSelector(({reservation}) => reservation)

    useDocumentTitle('Reservation Details')

    if (reservationState.loading) {
        return (<Loading loadingMessage="Fetching your reservation..."/>)
    }
    if (reservationState.error) {
        switch (reservationState.error) {
            case RESERVATION_NOT_FOUND:
                return (<Redirect to="/404"/>)
            default:
                // :todo Need to make a 500 error page, will do so in later ticket
                return (<Redirect to="/404"/>)
        }
    }

    let reservation
    let id

    if (!reservationState.selectedReservation
        || reservationState.selectedReservation.id !== window.location.pathname.split('/')[2]) {
        id = reservationId || window.location.pathname.split('/')[2]

        dispatch(fetchReservationDetails(id))

        return (<Loading loadingMessage="Fetching your reservation..."/>)
    } else {
        reservation = reservationState.selectedReservation
        id = reservation.id
    }

    const {
        property,
        propertyUnit,
        customer,
        notifications,
        customerId
    } = reservation

    return (
        <div className={ classes.pageWrapper }>
            <PageContainer heading="Reservation Details" breadcrumbs={ breadcrumbs }>
                <GridContainer>
                    <Grid item xs={ 12 }>
                        <div>
                            <ReservationInfo reservation={ reservation }/>

                            <StyledDivider/>

                            <PropertyInfo
                                propertyUnit={ propertyUnit }
                                property={ property }
                            />

                            <StyledDivider/>

                            <IdVerificationInfo
                                reservationId={id}
                            />

                            <StyledDivider/>

                            <AccessInfo reservationId={ id }/>

                            <StyledDivider/>

                            <CustomerInfo
                                customer={ customer }
                                customerId={ customerId }
                            />
                        </div>

                        { notifications?.length > 0 && (
                            <>
                                <StyledDivider/>
                                <Notifications messages={ notifications } reservationId={ id }/>
                            </>
                        ) }
                    </Grid>
                </GridContainer>
            </PageContainer>
        </div>
    )
}

export default ReservationDetails
